// @flow
import React, { Component } from "react";
import { graphql } from "@apollo/client/react/hoc";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { debounce, get, noop, flowRight as compose } from "lodash";
import mediator from "@tvg/mediator";
import {
  setFavoriteTrack,
  removeFavoriteTrack,
  type SetFavoriteTrackActionType,
  type RemoveFavoriteTrackActionType
} from "@tvg/shared-actions/UserActions";
import ufcClient from "@tvg/api/ufc";
import TopTracks from "@tvg/atomic-ui/_templates/TopTracks";
import type {
  TopTrack,
  FavoriteTopTracksSuccessResponse,
  TopTrackRace
} from "@tvg/types/Track";
import type { WagerProfile } from "@tvg/types/User";
import type { Device } from "@tvg/types/Device";
import type { BinaryFn, UnaryFn } from "@tvg/types/Functional";
import TopTracksQuery from "./graphql/queries/TopTracks.graphql";
import TopTracksRDAQuery from "./graphql/queries/TopTracksRDA.graphql";
import ApolloOptions from "./graphql/options.graph";

type Props = {
  device: Device,
  wagerProfile: WagerProfile,
  isLogged: boolean,
  accountId: string,
  isLoading: boolean,
  shouldUpdate: boolean,
  title: string,
  tracks: TopTrack[],
  userFavoriteTracks: String[],
  favoriteTracks: TopTrack[],
  featuredTracks: TopTrack[],
  setFavoriteTrack: BinaryFn<string, ?number, void>,
  removeFavoriteTrack: UnaryFn<string, void>,
  isMTPNewRules: boolean
};

type State = {
  firstRender: boolean,
  topTracks: TopTrack[],
  innerUpdate: boolean
};

export class TopTracksComponent extends Component<Props, State> {
  static defaultProps = {
    tracks: [],
    isLoading: false,
    device: "mobile",
    wagerProfile: "PORT-Generic",
    isLogged: false,
    accountId: -1,
    title: "Top Tracks",
    shouldUpdate: true,
    favoriteTracks: [],
    userFavoriteTracks: [],
    featuredTracks: [],
    setFavoriteTrack: noop,
    removeFavoriteTrack: noop,
    isMTPNewRules: false
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      firstRender: true,
      topTracks: [],
      innerUpdate: false
    };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const tracks = props.isLogged
      ? props.tracks
      : // $FlowFixMe
        props.featuredTracks.slice(0, 10);
    const defaultState = {
      firstRender: false,
      topTracks: tracks,
      innerUpdate: false
    };
    if (!props.isLoading && state.firstRender) {
      return defaultState;
    }
    return {
      ...state,
      innerUpdate: false,
      topTracks: state.innerUpdate ? state.topTracks : defaultState.topTracks
    };
  }

  onRaceSelect = (
    url: string,
    trackName: string,
    race: TopTrackRace,
    flags: string,
    isFavorite: boolean
  ) => {
    mediator.base.dispatch({
      type: "TOP_TRACKS:GO_TO_RACE",
      payload: {
        url: window.location.origin + url,
        trackName,
        race,
        flags,
        isFavorite
      }
    });

    if (this.props.device === "desktop") {
      mediator.base.dispatch({
        type: "TVG4_NAVIGATION",
        payload: { route: url }
      });
    }
  };

  handleFavoriteChange = (isAdding: boolean, trackCode: string) => {
    const track = this.state.topTracks.find(
      (tempTrack: TopTrack) => tempTrack.code === trackCode
    );
    if (track && isAdding && this.props.isLogged) {
      this.addTrackToFavorites(track);
    } else if (track && this.props.isLogged) {
      this.removeTrackFromFavorites(track);
    } else if (track) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: (error, success) =>
            /* istanbul ignore next */
            !error && success && this.addFavoriteAfterLoginWall(success, track),
          triggerAction: "top_tracks_favorite_change"
        }
      });
    }
  };

  /*
   * TODO: Fix success flow type
   * */
  addFavoriteAfterLoginWall = (success: any, track: TopTrack) => {
    if (get(success, "status", "fail") === "success") {
      const accountId = get(success, "data.userDetails.accountNumber", null);
      if (accountId) {
        ufcClient
          .addFavorite(accountId, "track", [get(track, "code")])
          .then(() => {
            mediator.base.dispatch({
              type: "TOP_TRACKS_FAVORITES_INTERACTION",
              payload: {
                isAdding: true,
                trackName: track.name
              }
            });
          });
      }
    }
  };

  updateStoredTracks = (
    trackCode: string,
    favoriteId: number,
    isAddingFavorite: boolean
  ) => {
    const updatedTopTracks: TopTrack[] = [...this.state.topTracks];
    const trackIndex = updatedTopTracks.findIndex(
      (track: TopTrack) => track.code === trackCode
    );
    if (isAddingFavorite) {
      updatedTopTracks[trackIndex] = {
        ...updatedTopTracks[trackIndex],
        favoriteId,
        favorite: { id: null },
        isFavorite: true
      };
    } else {
      updatedTopTracks[trackIndex] = {
        ...updatedTopTracks[trackIndex],
        favoriteId: null,
        favorite: { id: null },
        isFavorite: false
      };
    }
    mediator.base.dispatch({
      type: "UPDATE_SESSION_FAVORITE_TRACKS",
      payload: { isAddingFavorite, trackCode, favoriteId }
    });
    this.setState({
      topTracks: updatedTopTracks,
      innerUpdate: true
    });
  };

  addTrackToFavorites = (track: TopTrack): ?Promise<void> =>
    ufcClient
      .addFavorite(this.props.accountId, "track", [track.code])
      .then((resp: FavoriteTopTracksSuccessResponse) => {
        const favoriteId = get(resp, "data[0].favoriteId");
        this.updateStoredTracks(track.code, favoriteId, true);
        this.props.setFavoriteTrack(track.code, favoriteId);
        mediator.base.dispatch({
          type: "TOP_TRACKS_FAVORITES_INTERACTION",
          payload: {
            isAdding: true,
            trackName: track.name
          }
        });
      });

  removeTrackFromFavorites = (track: TopTrack): Promise<void> | false => {
    const favoriteId =
      get(track, "favorite.id", false) || get(track, "favoriteId", false);
    return (
      favoriteId &&
      ufcClient.removeFavorite(this.props.accountId, favoriteId).then(() => {
        this.updateStoredTracks(track.code, favoriteId, false);
        this.props.removeFavoriteTrack(track.code);
        mediator.base.dispatch({
          type: "TOP_TRACKS_FAVORITES_INTERACTION",
          payload: {
            isAdding: false,
            trackName: track.name
          }
        });
      })
    );
  };

  sendGTMScrollEvent = (scrolledBack: boolean) => {
    mediator.base.dispatch({
      type: "TOP_TRACKS_SCROLL_INTERACTION",
      payload: scrolledBack ? "Back" : "Next"
    });
  };

  /*
   * TODO: We shall change this this.state.topTracks.slice(0, 10) after the
   *   backend fixes this issues. RDA is returning uncapped favorites
   * */
  render() {
    return this.state.topTracks.length || this.props.isLoading ? (
      <TopTracks
        device={this.props.device}
        templateTitle={this.props.title}
        isLoading={this.props.isLoading && this.state.firstRender}
        topTracks={this.state.topTracks.slice(0, 10)}
        onRaceSelect={this.onRaceSelect}
        sendGTMScrollEvent={debounce(this.sendGTMScrollEvent, 150)}
        handleFavoriteChange={this.handleFavoriteChange}
        isMTPNewRules={this.props.isMTPNewRules}
      />
    ) : null;
  }
}

export const mapDispatchToProps = (
  dispatch: Dispatch<SetFavoriteTrackActionType | RemoveFavoriteTrackActionType>
) => ({
  setFavoriteTrack: (trackCode: string, favoriteId: number) =>
    dispatch(setFavoriteTrack(trackCode, favoriteId)),
  removeFavoriteTrack: (trackCode: string) =>
    dispatch(removeFavoriteTrack(trackCode))
});

export const skipTopTracksQuery = ({
  isLogged,
  shouldUpdate
}: {
  isLogged: boolean,
  shouldUpdate: boolean
}) => isLogged || !shouldUpdate;
export const skipRDAQuery = ({
  isLogged,
  shouldUpdate,
  accountId
}: {
  isLogged: boolean,
  shouldUpdate: boolean,
  accountId: number
}) => !isLogged || !shouldUpdate || !accountId;

export default connect(
  /* istanbul ignore next */
  (store) => ({
    wagerProfile: get(store, "userData.user.profile", "PORT-Generic"),
    isLogged: get(store, "userData.logged", false),
    accountId: get(store, "userData.user.accountNumber", -1),
    isMTPNewRules: get(store, "capi.featureToggles.MTPColoursRules", false)
  }),
  /* istanbul ignore next */
  mapDispatchToProps
)(
  compose(
    graphql(TopTracksQuery, {
      ...ApolloOptions,
      skip: skipTopTracksQuery
    }),
    graphql(TopTracksRDAQuery, {
      ...ApolloOptions,
      skip: skipRDAQuery
    })
  )(TopTracksComponent)
);
